import React, { useState, useEffect } from 'react';

/**
 * ShareMenu component
 * - Single clean trigger button: "Videresend"
 * - Opens a popup modal with:
 *   1) Normalt link (kopiering)
 *   2) Facebook
 *   3) LinkedIn
 *   4) Instagram
 *   5) TikTok
 *   6) Videresend direkte til en anden (via e-mail eller WhatsApp, hvor linket integreres pænt)
 */
export default function ShareMenu({ title, subtitle, url, align = 'right' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('quick'); // 'quick' | 'direct'
  const [copied, setCopied] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [personalNote, setPersonalNote] = useState('');
  const [forwardSent, setForwardSent] = useState(false);

  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title || 'Artikel på Skatunderlup');

  // Close popup on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3500);
  };

  const copyToClipboard = async (customToast = 'Linket er kopieret til udklipsholderen') => {
    let success = false;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        success = true;
      }
    } catch {
      // Fallback
    }

    if (!success) {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = shareUrl;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        textarea.setAttribute('readonly', '');
        document.body.appendChild(textarea);
        textarea.select();
        textarea.setSelectionRange(0, 99999);
        success = document.execCommand('copy');
        document.body.removeChild(textarea);
      } catch {
        success = false;
      }
    }

    // Altid giv brugeren en bekræftelse på at linket er taget/markeret, så knappen bliver grøn
    setCopied(true);
    showToast(customToast);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleFacebook = () => {
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    window.open(fbUrl, '_blank', 'noopener,noreferrer,width=640,height=580');
  };

  const handleLinkedIn = () => {
    const liUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
    window.open(liUrl, '_blank', 'noopener,noreferrer,width=640,height=580');
  };

  const handleInstagram = () => {
    copyToClipboard('Linket er kopieret! Åbner Instagram, hvor du kan indsætte det i en besked eller story.');
    setTimeout(() => {
      window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
    }, 600);
  };

  const handleTikTok = () => {
    copyToClipboard('Linket er kopieret! Åbner TikTok, så du nemt kan dele artiklen.');
    setTimeout(() => {
      window.open('https://www.tiktok.com/', '_blank', 'noopener,noreferrer');
    }, 600);
  };

  const handleSendToOther = (e) => {
    e?.preventDefault();
    const subject = encodeURIComponent(`Læs denne artikel: ${title || 'Skatunderlup'}`);
    const noteText = personalNote ? `${personalNote}\n\n` : '';
    const greeting = recipientName ? `Kære ${recipientName},\n\n` : 'Hej,\n\n';
    const body = encodeURIComponent(
      `${greeting}${noteText}Jeg vil gerne dele denne artikel fra Skatunderlup med dig:\n\n"${title}"\n${subtitle ? subtitle + '\n\n' : '\n'}Læs hele artiklen her:\n${shareUrl}\n\nVenlig hilsen`
    );

    const mailto = recipientEmail
      ? `mailto:${encodeURIComponent(recipientEmail)}?subject=${subject}&body=${body}`
      : `mailto:?subject=${subject}&body=${body}`;

    window.location.href = mailto;
    setForwardSent(true);
    setTimeout(() => {
      setForwardSent(false);
      setIsOpen(false);
    }, 2000);
  };

  const handleWhatsApp = () => {
    const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(`Hej! Læs denne artikel: "${title}" ${shareUrl}`)}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="relative inline-block text-left">
      {/* Toast Notification */}
      {toastMsg && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm rounded-md bg-[#010b27] px-4 py-3 text-xs text-white shadow-xl border border-slate-700 flex items-center gap-2.5 animate-in fade-in slide-in-from-bottom-2 duration-200">
          <i className="fa-solid fa-circle-check text-[#fd7302]" aria-hidden="true" />
          <span className="leading-snug">{toastMsg}</span>
        </div>
      )}

      {/* Primær knap - viser "Del" */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center gap-2 rounded-xs bg-[#fd7302] hover:bg-[#e06500] text-white px-3.5 py-1.5 text-xs font-bold shadow-2xs transition-colors cursor-pointer"
        title="Del eller videresend denne artikel"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <i className="fa-solid fa-share-nodes text-xs" aria-hidden="true" />
        <span>Del</span>
      </button>

      {/* Delings-popup (Modal) der dukker op ved klik */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-md border border-slate-200 bg-white p-5 sm:p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-150 text-slate-900"
            role="dialog"
            aria-modal="true"
            aria-labelledby="share-popup-title"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between border-b border-slate-100 pb-3">
              <div>
                <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#fd7302]">
                  <i className="fa-solid fa-share-nodes" aria-hidden="true" />
                  <span>Del & videresend</span>
                </div>
                <h3 id="share-popup-title" className="font-serif text-lg font-bold text-[#010b27]">
                  Videresend artikel
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-slate-400 hover:text-slate-700 p-1 rounded transition-colors"
                aria-label="Luk vindue"
              >
                <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
              </button>
            </div>

            {/* Artikel teaser / overskrift */}
            <div className="mt-3.5 rounded-xs border border-slate-200 bg-slate-50 p-3 text-xs">
              <span className="font-bold text-[#010b27] block truncate">
                {title || 'Artikel på Skatunderlup'}
              </span>
              {subtitle && (
                <span className="text-slate-600 line-clamp-1 mt-0.5 block italic text-[11px]">
                  {subtitle}
                </span>
              )}
            </div>

            {/* Faneblade / valgmuligheder */}
            <div className="mt-4 flex border-b border-slate-200 text-xs font-semibold">
              <button
                type="button"
                onClick={() => setActiveTab('quick')}
                className={`flex-1 py-2 text-center border-b-2 transition-colors ${
                  activeTab === 'quick'
                    ? 'border-[#fd7302] text-[#010b27] font-bold'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                Delingskanaler & link
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('direct')}
                className={`flex-1 py-2 text-center border-b-2 transition-colors ${
                  activeTab === 'direct'
                    ? 'border-[#fd7302] text-[#010b27] font-bold'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                Videresend til en anden
              </button>
            </div>

            {/* TAB 1: Kopiér link + sociale medier */}
            {activeTab === 'quick' && (
              <div className="mt-4 space-y-4">
                {/* Normalt link felt med kopiér-knap */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Normalt link
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={shareUrl}
                      className="w-full rounded-xs border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 select-all focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => copyToClipboard('Linket er kopieret!')}
                      className={`shrink-0 inline-flex items-center gap-1.5 rounded-xs px-3 py-2 text-xs font-semibold transition-colors ${
                        copied
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                          : 'bg-[#010b27] hover:bg-slate-800 text-white'
                      }`}
                      title="Kopiér link"
                    >
                      <i className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'}`} aria-hidden="true" />
                      <span>{copied ? 'Kopieret' : 'Kopiér'}</span>
                    </button>
                  </div>
                </div>

                {/* Sociale platforme */}
                <div>
                  <div className="text-xs font-semibold text-slate-700 mb-2">
                    Del på sociale platforme
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {/* Facebook */}
                    <button
                      type="button"
                      onClick={handleFacebook}
                      className="flex items-center gap-2.5 rounded-xs border border-blue-100 bg-[#1877f2]/10 hover:bg-[#1877f2]/20 text-[#1877f2] p-2.5 text-xs font-semibold transition-colors"
                    >
                      <i className="fa-brands fa-facebook text-base" aria-hidden="true" />
                      <span>Facebook</span>
                    </button>

                    {/* LinkedIn */}
                    <button
                      type="button"
                      onClick={handleLinkedIn}
                      className="flex items-center gap-2.5 rounded-xs border border-sky-100 bg-[#0a66c2]/10 hover:bg-[#0a66c2]/20 text-[#0a66c2] p-2.5 text-xs font-semibold transition-colors"
                    >
                      <i className="fa-brands fa-linkedin text-base" aria-hidden="true" />
                      <span>LinkedIn</span>
                    </button>

                    {/* Instagram */}
                    <button
                      type="button"
                      onClick={handleInstagram}
                      className="flex items-center gap-2.5 rounded-xs border border-pink-100 bg-[#e1306c]/10 hover:bg-[#e1306c]/20 text-[#e1306c] p-2.5 text-xs font-semibold transition-colors"
                    >
                      <i className="fa-brands fa-instagram text-base" aria-hidden="true" />
                      <span>Instagram</span>
                    </button>

                    {/* TikTok */}
                    <button
                      type="button"
                      onClick={handleTikTok}
                      className="flex items-center gap-2.5 rounded-xs border border-slate-200 bg-slate-900/5 hover:bg-slate-900/10 text-slate-900 p-2.5 text-xs font-semibold transition-colors"
                    >
                      <i className="fa-brands fa-tiktok text-base" aria-hidden="true" />
                      <span>TikTok</span>
                    </button>
                  </div>
                </div>

                <div className="pt-2 text-center">
                  <button
                    type="button"
                    onClick={() => setActiveTab('direct')}
                    className="inline-flex items-center gap-1.5 text-xs text-[#fd7302] hover:underline font-semibold"
                  >
                    <span>Vil du sende direkte til en persons mail eller WhatsApp? Klik her</span>
                    <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}

            {/* TAB 2: Videresend til en anden (e-mail & WhatsApp) */}
            {activeTab === 'direct' && (
              <form onSubmit={handleSendToOther} className="mt-4 space-y-3">
                <p className="text-xs text-slate-600 leading-relaxed">
                  Videresend artiklen direkte, så modtageren får en færdig overskrift og adgang uden besvær.
                </p>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Modtagers navn (valgfrit)
                  </label>
                  <input
                    type="text"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    placeholder="F.eks. Hanne"
                    className="w-full rounded-xs border border-slate-200 px-3 py-2 text-xs text-slate-900 focus:border-[#fd7302] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Modtagers e-mail (valgfrit – åbner dit mailprogram)
                  </label>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="f.eks. kollega@virksomhed.dk"
                    className="w-full rounded-xs border border-slate-200 px-3 py-2 text-xs text-slate-900 focus:border-[#fd7302] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Personlig hilsen (valgfrit)
                  </label>
                  <textarea
                    rows={2}
                    value={personalNote}
                    onChange={(e) => setPersonalNote(e.target.value)}
                    placeholder="F.eks.: 'Se lige denne artikel om skat...'"
                    className="w-full rounded-xs border border-slate-200 px-3 py-2 text-xs text-slate-900 focus:border-[#fd7302] focus:outline-none"
                  />
                </div>

                <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
                  <button
                    type="button"
                    onClick={handleWhatsApp}
                    className="inline-flex items-center justify-center gap-1.5 rounded-xs border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 px-3 py-2 text-xs font-semibold transition-colors"
                  >
                    <i className="fa-brands fa-whatsapp text-sm text-emerald-600" aria-hidden="true" />
                    <span>WhatsApp</span>
                  </button>

                  <button
                    type="submit"
                    className="inline-flex items-center justify-center gap-1.5 rounded-xs bg-[#fd7302] hover:bg-[#e06500] text-white px-4 py-2 text-xs font-bold transition-colors cursor-pointer"
                  >
                    <i className={`fa-solid ${forwardSent ? 'fa-check' : 'fa-paper-plane'}`} aria-hidden="true" />
                    <span>{forwardSent ? 'Åbner mail...' : 'Videresend via e-mail'}</span>
                  </button>
                </div>
              </form>
            )}

            {/* Footer luk-knap */}
            <div className="mt-5 pt-3 border-t border-slate-100 flex justify-end">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="rounded-xs border border-slate-200 bg-white hover:bg-slate-50 px-3.5 py-1.5 text-xs font-semibold text-slate-700 transition-colors"
              >
                Luk
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
